import React from "react";
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
} from "@mui/material";

import type { FormFieldSize } from "../../types";

export interface CheckboxProps extends Omit<
  MuiCheckboxProps,
  // Locked: the glyphs come from the design system's icon set, and styling
  // is owned by tokens and the props below rather than per-instance `sx`.
  "size" | "sx" | "icon" | "checkedIcon" | "indeterminateIcon"
> {
  /** Checkbox size. Default: 'sm' */
  size?: FormFieldSize;
  /** Renders the indeterminate icon in place of checked/unchecked. Passed through to MUI. */
  indeterminate?: boolean;
  /** Disables the touch ripple effect. Passed through to MUI. */
  disableRipple?: boolean;
}

const sizeMap: Record<FormFieldSize, MuiCheckboxProps["size"]> = {
  sm: "small",
  md: "medium",
};

/** Wraps MUI Checkbox; `size` maps to the design system's sm/md scale and `color` is constrained to the MUI palette roles. */
// The ref type is taken from MUI rather than named outright: the root renders
// as a `<span>`, but MUI inherits ButtonBase's typing and declares it an
// `HTMLButtonElement`, so naming either one would be wrong somewhere.
export const Checkbox = React.forwardRef<
  React.ComponentRef<typeof MuiCheckbox>,
  CheckboxProps
>(({ size = "sm", color = "primary", ...rest }, ref) => (
  <MuiCheckbox
    data-testid="ds-checkbox"
    ref={ref}
    size={sizeMap[size]}
    color={color}
    {...rest}
  />
));

Checkbox.displayName = "Checkbox";
